import { AutoAwesomeOutlined, Chat, ChatOutlined, Edit, FilterList, TextSnippetOutlined } from "@mui/icons-material";
import { Badge, Box, Button, Card, CircularProgress, Link, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import Feedback from "../Feedback";
import { ChatInteraction, FocusedInteraction } from "../models";
import PMREvidences from "../PMREvidences";
import useMarketResearchStore from "../useMarketResearchStore";
import MarkdownRenderer from "../../common/MarkdownRenderer";
import { scrollQuestionIntoView } from "../../../utils/scrollQuestionIntoView";
import ThinkingSteps from "../../common/ThinkingSteps";
import { ThinkingStep } from "../../Deepchat/models";
import { grey, indigo } from "../../../colors";

interface ChatAnswerNodeProps {
  interaction: ChatInteraction;
  anchorId: string;
  isLoading: boolean;
  loadingStatus: string;
  fromSummary?: boolean;
  numOfResponses?: number;
  thinkingSteps?: ThinkingStep[];
  thinkingStepsCollapsed?: boolean;
  onToggleThinkingStepsCollapse?: () => void;
}

export default function ChatAnswerNode({
  interaction,
  anchorId,
  isLoading,
  loadingStatus,
  fromSummary = false,
  numOfResponses = undefined,
  thinkingSteps: steps = [],
  thinkingStepsCollapsed = true,
  onToggleThinkingStepsCollapse,
}: ChatAnswerNodeProps) {
  const { editQuestion, filterCount } = useMarketResearchStore();
  const [focusedInteraction, setFocusedInteraction] = useAtom(FocusedInteraction);

  const { ref, inView } = useInView({ threshold: 0.8 });

  useEffect(() => {
    if (inView && focusedInteraction !== interaction) {
      setFocusedInteraction(interaction);
    }
  }, [inView]);

  return (
    <Box>
      {!fromSummary && (
        <>
          <Box
            sx={{ display: "flex", marginTop: "1rem", flex: 1 }}
            justifyContent={"space-between"}
            ref={ref}
            id={anchorId}
          >
            <Link
              href={`${location.pathname}#${anchorId}`}
              underline="none"
              onClick={(event) => {
                event.preventDefault();
                scrollQuestionIntoView(anchorId);
              }}
            >
              <Box sx={{ display: "flex" }} alignItems={"center"}>
                <Chat />
                <Typography variant="h5" sx={{ marginLeft: "0.5rem" }}>
                  Original Question
                </Typography>
              </Box>
            </Link>
            <Box sx={{ display: "flex" }} alignItems={"center"}>
              <Button
                size="small"
                startIcon={
                  <Badge
                    variant="standard"
                    badgeContent={filterCount()}
                    color="secondary"
                    sx={{
                      display: "none",
                      "& .MuiBadge-badge": {
                        color: "white",
                      },
                    }}
                  >
                    <FilterList />
                  </Badge>
                }
              />
              <span onClick={() => editQuestion(interaction.user_query ?? "")} style={{ cursor: "pointer" }}>
                <Edit />
              </span>
            </Box>
          </Box>
          <Card sx={{ marginTop: "1rem", display: "flex", gap: "20px", padding: "1rem", backgroundColor: grey[100] }}>
            {numOfResponses && (
              <Badge
                badgeContent={numOfResponses}
                color="secondary"
                sx={{
                  "& .MuiBadge-badge": {
                    color: "white",
                    width: "1rem",
                    height: "1rem",
                  },
                }}
              >
                <ChatOutlined fontSize="medium" sx={{ color: indigo[700] }} />
              </Badge>
            )}
            <Typography sx={{ fontWeight: "700", fontSize: "14px" }}>{interaction.user_query}</Typography>
          </Card>
        </>
      )}

      {steps.length > 0 && (
        <Box sx={{ mt: "2rem" }}>
          <ThinkingSteps
            steps={steps}
            collapsed={thinkingStepsCollapsed}
            onToggleCollapse={onToggleThinkingStepsCollapse ?? (() => {})}
          />
        </Box>
      )}

      <Box sx={{ alignItems: "center", mt: "2rem" }}>
        <Box>
          <Box sx={{ display: "flex" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flex: 1 }}>
              <Box>
                <AutoAwesomeOutlined sx={{ fill: indigo[900], verticalAlign: "sub", marginRight: "0.5em" }} />
                <Typography variant="h6" color={indigo[900]} display="inline-block">
                  {fromSummary ? "Summary" : "PMR Answer"}
                </Typography>
              </Box>
              <Box sx={{ marginLeft: "1em", marginTop: "0.25em", flex: 1 }}>
                {isLoading && (
                  <>
                    <CircularProgress
                      size="small"
                      sx={{ gridArea: "1/1", width: "1.25rem", height: "1.25rem", verticalAlign: "sub" }}
                    />
                    <Typography sx={{ marginLeft: "0.5em", fontStyle: "italic", display: "inline-block" }}>
                      {loadingStatus}
                    </Typography>
                  </>
                )}
              </Box>
              <Box>
                <Feedback interactionId={fromSummary ? interaction.action_id! : interaction.user_chat_session} />
              </Box>
            </Box>
          </Box>
          <Card sx={{ marginTop: "1rem", marginBottom: "1rem", borderRadius: "1em" }}>
            <Box sx={{ padding: "1rem" }}>
              <MarkdownRenderer text={interaction.answer ?? ""} />
            </Box>
          </Card>
        </Box>
      </Box>

      {interaction?.evidence?.length && !fromSummary && (
        <Box sx={{ display: "flex", flexDirection: "column", mt: "2rem", gap: "0.25rem" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <TextSnippetOutlined />
            <Typography variant="h6" color={indigo[900]} sx={{ fontSize: "20px" }}>
              Relevant Text Records
            </Typography>
          </Box>
          <Box sx={{ pb: "1rem" }}>
            <PMREvidences evidences={interaction?.evidence ?? []} />
          </Box>
        </Box>
      )}
    </Box>
  );
}
